/**
 * Descoberta de quadros: busca (com cache, TTL, nova tentativa e cancelamento)
 * a listagem do diretório de cada produto no STAR e guarda o resultado num store.
 */
import { create } from 'zustand';
import { fetchWithRetry, isAbortError } from '../lib/fetchRetry';
import { parseListing, type Listing } from './listing';
import { productDirUrl } from './star';

/** Validade da listagem em cache antes de uma nova busca (ms). */
export const LISTING_TTL_MS = 60_000;
/** Tempo-limite por tentativa da listagem: 1ª rápida (servidor travado vira erro logo), 2ª mais folgada. */
export const LISTING_TIMEOUTS_MS = [12_000, 20_000];
/** A partir daqui, a busca é marcada como "demorando mais que o normal". */
export const LISTING_SLOW_MS = 8_000;

export type CatalogStatus = 'loading' | 'ready' | 'error';

export interface CatalogEntry {
  listing: Listing | null;
  status: CatalogStatus;
  /** Quando a listagem atual foi obtida (epoch ms), ou 0. */
  fetchedAt: number;
  error: string | null;
  /** A busca em andamento passou de LISTING_SLOW_MS. */
  slow?: boolean;
}

interface CatalogState {
  entries: Record<string, CatalogEntry>;
  /** Última verificação bem-sucedida de qualquer produto (epoch ms). */
  lastCheck: number | null;
}

export const useCatalogStore = create<CatalogState>()(() => ({ entries: {}, lastCheck: null }));

function patchEntry(productId: string, patch: Partial<CatalogEntry>): void {
  useCatalogStore.setState((s) => {
    const prev: CatalogEntry = s.entries[productId] ?? { listing: null, status: 'loading', fetchedAt: 0, error: null };
    return { entries: { ...s.entries, [productId]: { ...prev, ...patch } } };
  });
}

const inFlight = new Map<string, Promise<Listing>>();

/** Anexa um AbortSignal a uma promessa compartilhada (só o chamador desiste). */
function withSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  return new Promise<T>((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException('Operação cancelada', 'AbortError'));
    const onAbort = () => reject(new DOMException('Operação cancelada', 'AbortError'));
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
}

export interface LoadListingOptions {
  /** Ignora o cache (TTL) e busca de novo. */
  force?: boolean;
  signal?: AbortSignal;
}

/**
 * Busca a listagem do produto. Chamadas simultâneas compartilham a mesma
 * requisição; resultados recentes (< TTL) vêm do cache.
 */
export function loadListing(productId: string, opts: LoadListingOptions = {}): Promise<Listing> {
  const entry = useCatalogStore.getState().entries[productId];
  if (!opts.force && entry?.listing && Date.now() - entry.fetchedAt < LISTING_TTL_MS) {
    return Promise.resolve(entry.listing);
  }
  let promise = inFlight.get(productId);
  if (!promise) {
    patchEntry(productId, { status: entry?.listing ? 'ready' : 'loading', slow: false });
    const slowTimer = setTimeout(() => patchEntry(productId, { slow: true }), LISTING_SLOW_MS);
    promise = (async () => {
      try {
        const html = await fetchWithRetry(
          productDirUrl(productId),
          { cache: 'no-cache' },
          { retries: 1, timeouts: LISTING_TIMEOUTS_MS, read: (r) => r.text() },
        );
        const listing = parseListing(html, productId);
        if (listing.frames.length === 0) throw new Error('Listagem sem quadros');
        const now = Date.now();
        patchEntry(productId, { listing, status: 'ready', fetchedAt: now, error: null });
        useCatalogStore.setState({ lastCheck: now });
        return listing;
      } catch (e) {
        if (!isAbortError(e)) {
          patchEntry(productId, { status: 'error', error: e instanceof Error ? e.message : String(e) });
        }
        throw e;
      } finally {
        clearTimeout(slowTimer);
        if (useCatalogStore.getState().entries[productId]?.slow) patchEntry(productId, { slow: false });
        inFlight.delete(productId);
      }
    })();
    inFlight.set(productId, promise);
  }
  return withSignal(promise, opts.signal);
}

export function getListing(productId: string): Listing | null {
  return useCatalogStore.getState().entries[productId]?.listing ?? null;
}
